import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  CalendarCheck,
  Clock,
  Receipt,
  FileText,
  Plus,
  ArrowRight,
  CheckCircle,
  Briefcase,
} from "phosphor-react";
import { useEntitlementsWithFetch } from "../contexts/EntitlementContext";
import { UsageLimitWarning, FeatureActiveContent } from "../components/FeatureCTA";
import AdBanner from "../components/AdBanner";
import BottomQuickActions from "../components/BottomQuickActions";
import { CustomAdSlot } from "../components/CustomAdSlot";
import { format } from "date-fns";
import { formatCurrency } from "../utils/formatters";
import { useAuth } from "../App";
import OnboardingCard from "../components/OnboardingCard";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // REMOVED: paymentStatus state - now handled by global PaymentsActivationBanner in AppLayout
  
  // Fetch entitlements when dashboard loads (user is authenticated at this point)
  useEntitlementsWithFetch();

  // ============================================
  // GATED FETCH (P0 POLISH)
  // ============================================
  // Only fetch contractor dashboard if:
  // 1) Auth has resolved (not loading)
  // 2) User exists and is NOT a technician
  // 3) Current path is NOT under /tech
  // This prevents spurious 401s during cross-tab auth switches
  useEffect(() => {
    // Don't fetch if auth is still loading
    if (authLoading) {
      return;
    }
    
    // Don't fetch if no user (will redirect to login)
    if (!user) {
      setLoading(false);
      return;
    }
    
    // Don't fetch if user is a technician (wrong shell)
    if (user.role === 'technician') {
      console.log('[Dashboard] Skipping fetch - user is technician');
      setLoading(false);
      return;
    }
    
    // Don't fetch if we're on /tech path (wrong shell)
    if (location.pathname.startsWith('/tech')) {
      console.log('[Dashboard] Skipping fetch - on /tech path');
      setLoading(false);
      return;
    }
    
    fetchDashboard();
    // REMOVED: fetchPaymentStatus() - now handled by global PaymentsActivationBanner in AppLayout
  }, [authLoading, user, location.pathname]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/dashboard`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const dashboardData = await response.json();
        setData(dashboardData);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };
  
  // PRESERVED (DISABLED): Dashboard-local payment status fetch
  // Now handled globally by PaymentsActivationBanner in AppLayout
  // const fetchPaymentStatus = async () => { ... };

  if (loading || authLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-32 bg-zinc-200 rounded-sm" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-zinc-200 rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Onboarding Card - First-time setup experience */}
      <OnboardingCard />
      
      {/* Usage Alert - Only show when Unlimited Usage feature is globally active */}
      <FeatureActiveContent featureSlug="unlimited_usage">
        <UsageLimitWarning 
          currentUsage={data?.monthly_jobs || 0} 
          limitType="job_limit" 
        />
      </FeatureActiveContent>
      
      {/* PRESERVED (DISABLED): Dashboard-local payment status banners
       * Now handled globally by PaymentsActivationBanner in AppLayout
       * Removed to avoid duplicate banners across pages
       * 
       * Original implementation displayed:
       * - STATE 1a: Onboarding INCOMPLETE (finish CTA)
       * - STATE 1b: Onboarding SUBMITTED (under review, no CTA)
       * - STATE 2: Approved but not activated (activate CTA)
       */}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-2 border-zinc-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Today's Visits</p>
                <p className="font-heading text-3xl font-bold mt-1">
                  {data?.todays_visits?.length || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-50 border-2 border-blue-200 flex items-center justify-center">
                <CalendarCheck size={24} className="text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-zinc-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Overdue Invoices</p>
                <p className="font-heading text-3xl font-bold mt-1 text-red-500">
                  {formatCurrency(data?.overdue_total || 0, false)}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-50 border-2 border-red-200 flex items-center justify-center">
                <Receipt size={24} className="text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-zinc-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Uninvoiced Jobs</p>
                <p className="font-heading text-3xl font-bold mt-1 text-amber-500">
                  {data?.uninvoiced_jobs?.length || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-50 border-2 border-amber-200 flex items-center justify-center">
                <Briefcase size={24} className="text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-zinc-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Pending Estimates</p>
                <p className="font-heading text-3xl font-bold mt-1 text-emerald-500">
                  {data?.pending_estimates?.length || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center">
                <FileText size={24} className="text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Jobs Completed This Month Card */}
      <Card className="border-2 border-zinc-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center">
                <CheckCircle size={24} className="text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Jobs Completed This Month</p>
                <p className="font-heading text-2xl font-bold mt-1">
                  {(() => {
                    const now = new Date();
                    const currentMonth = now.getMonth();
                    const currentYear = now.getFullYear();
                    const completedCount = (data?.recent_jobs || []).filter(job => {
                      if (job.status !== 'completed') return false;
                      const completionDate = job.completed_at ? new Date(job.completed_at) : (job.updated_at ? new Date(job.updated_at) : null);
                      if (!completionDate) return false;
                      return completionDate.getMonth() === currentMonth && completionDate.getFullYear() === currentYear;
                    }).length;
                    return completedCount;
                  })()} Completed
                </p>
              </div>
            </div>
            <Link to="/jobs">
              <Button
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
                data-testid="new-job-btn"
              >
                <Plus size={18} className="mr-2" />
                New Job
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Ad Banner - Only for contractors with zero paid features/bundles */}
      <AdBanner placement="dashboard_footer" />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Today's Visits */}
        <Card className="border-2 border-zinc-200">
          <CardHeader className="border-b border-zinc-100 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg font-semibold uppercase">
                Today's Visits
              </CardTitle>
              <Link to="/calendar">
                <Button variant="ghost" size="sm" className="text-[#FF6600]">
                  View Calendar
                  <ArrowRight size={16} className="ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {data?.todays_visits?.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {data.todays_visits.slice(0, 5).map((visit) => (
                  <div
                    key={visit.visit_id}
                    className="p-4 hover:bg-zinc-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{visit.job?.title || "Visit"}</p>
                        <p className="text-sm text-zinc-500">
                          {visit.customer?.name} • {format(new Date(visit.scheduled_start), "h:mm a")}
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={`status-${visit.status}`}
                      >
                        {visit.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-zinc-400">
                <Clock size={32} className="mx-auto mb-2 opacity-50" />
                <p>No visits scheduled for today</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Jobs */}
        <Card className="border-2 border-zinc-200">
          <CardHeader className="border-b border-zinc-100 pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg font-semibold uppercase">
                Recent Jobs
              </CardTitle>
              <Link to="/jobs">
                <Button variant="ghost" size="sm" className="text-[#FF6600]">
                  View All
                  <ArrowRight size={16} className="ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {data?.recent_jobs?.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {data.recent_jobs.map((job) => (
                  <Link
                    key={job.job_id}
                    to={`/jobs/${job.job_id}`}
                    className="block p-4 hover:bg-zinc-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{job.title}</p>
                        <p className="text-sm text-zinc-500">
                          {job.customer?.name}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`status-${job.status}`}
                        >
                          {job.status}
                        </Badge>
                        {job.invoiced && (
                          <CheckCircle size={16} className="text-emerald-500" />
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-zinc-400">
                <Briefcase size={32} className="mx-auto mb-2 opacity-50" />
                <p>No jobs yet. Create your first job!</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Custom Ad Slot - Contractor Dashboard Sidebar */}
      <CustomAdSlot 
        placementKey="contractor_dashboard_sidebar" 
        className="mt-6"
      />

      {/* Quick Actions */}
      <BottomQuickActions />
    </div>
  );
}
